import { DEFAULT_THANK_YOU } from '../pages/DonationThankYou';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, MessageCircle, Send, Facebook, Instagram, Youtube, Twitter, Linkedin, Phone, Mail, MapPin, Lock, User, Eye, EyeOff, Image as ImageIcon, Receipt, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ImageUpload } from './ImageUpload';
import { invalidateSettingsCache } from '../hooks/useSiteSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SiteSettingsEditor = () => {
  const { user, checkAuth } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [emailForm, setEmailForm] = useState({ current_password: '', new_email: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/settings`);
      setSettings(data.settings || {});
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/settings`,
        { settings },
        { withCredentials: true }
      );
      invalidateSettingsCache();
      toast.success('Site settings saved successfully!');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: passwordForm.current, new_password: passwordForm.new },
        { withCredentials: true }
      );
      toast.success('Password changed successfully!');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setChangingEmail(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-email`,
        emailForm,
        { withCredentials: true }
      );
      toast.success('Admin email changed successfully!');
      setEmailForm({ current_password: '', new_email: '' });
      await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change email');
    } finally {
      setChangingEmail(false);
    }
  };

  const socialFields = [
    { key: 'whatsapp', label: 'WhatsApp Number', placeholder: '919895959558 (with country code, no + or spaces)', icon: <MessageCircle size={20} className="text-[#25D366]" /> },
    { key: 'telegram', label: 'Telegram Username', placeholder: 'rishidevfoundation (no @ symbol)', icon: <Send size={20} className="text-[#0088CC]" /> },
    { key: 'facebook', label: 'Facebook Page URL', placeholder: 'https://facebook.com/yourpage', icon: <Facebook size={20} className="text-[#1877F2]" /> },
    { key: 'instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/rishidevfoundation', icon: <Instagram size={20} className="text-[#E1306C]" /> },
    { key: 'youtube', label: 'YouTube Channel URL', placeholder: 'https://youtube.com/@channel', icon: <Youtube size={20} className="text-[#FF0000]" /> },
    { key: 'twitter', label: 'Twitter/X URL', placeholder: 'https://twitter.com/handle', icon: <Twitter size={20} className="text-charcoal-800" /> },
    { key: 'linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/company/rishidevfoundation', icon: <Linkedin size={20} className="text-[#0A66C2]" /> },
  ];

  const gallerySettings = [
    { key: 'youtube_channel_url', label: 'YouTube Channel URL', placeholder: 'https://www.youtube.com/@RishidevFoundation', icon: <Youtube size={20} className="text-[#FF0000]" /> },
  ];

  const contactFields = [
    { key: 'phone_primary', label: 'Primary Phone', placeholder: '+91 98959 59558', icon: <Phone size={20} className="text-saffron-600" /> },
    { key: 'phone_secondary', label: 'Secondary Phone (optional)', placeholder: '+91 98765 43210', icon: <Phone size={20} className="text-saffron-600" /> },
    { key: 'contact_email', label: 'Contact Email', placeholder: 'info@rishidevfoundation.in', icon: <Mail size={20} className="text-saffron-600" /> },
  ];

  const addressFields = [
    {
      key: 'registered_address',
      label: 'Registered Office Address',
      placeholder: '35/1623, 2nd Floor, Jubilee Bazar, Ondane Road,\nKannur-1, Kerala, India, 670001',
      icon: <MapPin size={20} className="text-saffron-600" />,
    },
    {
      key: 'admin_address',
      label: 'Administrative Office Address',
      placeholder: 'Vins Villa, Kadungallur Road, Thottakkattukara,\nAluva, Ernakulam, Kerala – 683108, India',
      icon: <MapPin size={20} className="text-saffron-600" />,
    },
  ];

  if (loading) {
    return <div className="text-center py-12 text-charcoal-500">Loading settings...</div>;
  }

  return (
    <div className="space-y-6" data-testid="site-settings-editor">
      <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
        <h3 className="text-lg font-heading text-saffron-700 mb-4 pb-2 border-b border-saffron-100 flex items-center gap-2">
          <ImageIcon size={20} /> Site Logo
        </h3>
        <ImageUpload
          label="Logo Image"
          recommendedSize="512×512 (1:1 square) or 400×400 minimum, transparent PNG recommended"
          value={settings.logo_url}
          onChange={(v) => handleChange('logo_url', v)}
          testId="settings-logo"
        />
        <p className="text-xs text-charcoal-500 mt-2">
          This logo appears in the site header, footer, and anywhere the logo is displayed. Click "Save All Changes" below to apply.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
        <h3 className="text-lg font-heading text-saffron-700 mb-4 pb-2 border-b border-saffron-100 flex items-center gap-2">
          <Receipt size={20} /> Tax Exemption (80G / 12A)
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">
              80(G) Registration Number
            </label>
            <input
              type="text"
              value={settings.tax_exemption_number || ''}
              onChange={(e) => handleChange('tax_exemption_number', e.target.value)}
              placeholder="e.g., AAATR1234E-2023-24-11017"
              data-testid="settings-tax-exemption"
              className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">
              12A Registration Number
            </label>
            <input
              type="text"
              value={settings.reg_12a_number || ''}
              onChange={(e) => handleChange('reg_12a_number', e.target.value)}
              placeholder="e.g., AAATR1234E/12A/2023"
              data-testid="settings-reg-12a"
              className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">
            Donation Thank-You Message from Guruji
          </label>
          <textarea
            rows={5}
            value={settings.donation_thank_you_message || ''}
            onChange={(e) => handleChange('donation_thank_you_message', e.target.value)}
            placeholder={DEFAULT_THANK_YOU}
            data-testid="settings-thank-you-message"
            className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
          />
          <p className="text-xs text-charcoal-500 mt-1">Shown to donors after a successful donation. Leave blank to use the default message.</p>
        </div>
        <p className="text-xs text-charcoal-500 mt-3">
          Both numbers appear on 80(G) PDF receipts issued to donors. Leave blank to show a placeholder until registration is finalised.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl font-heading text-charcoal-800">Social Media & Contact</h2>
            <p className="text-sm text-charcoal-500 mt-1">
              Update contact numbers, social handles, email, and address shown across the site.
            </p>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            data-testid="settings-save-button"
            className="flex items-center gap-2 bg-saffron-600 text-white rounded-lg px-6 py-2 hover:bg-saffron-500 transition-colors disabled:opacity-50"
          >
            <Save size={18} /> {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>

        <div>
          <h3 className="text-lg font-heading text-saffron-700 mb-4 pb-2 border-b border-saffron-100">
            Social Media Accounts
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {socialFields.map((field) => (
              <div key={field.key}>
                <label className="flex items-center gap-2 text-sm font-body font-medium text-charcoal-700 mb-1.5">
                  {field.icon} {field.label}
                </label>
                <input
                  type="text"
                  value={settings[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  data-testid={`settings-${field.key}`}
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-lg font-heading text-saffron-700 mb-4 pb-2 border-b border-saffron-100">
            Gallery & Video Integrations
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {gallerySettings.map((field) => (
              <div key={field.key}>
                <label className="flex items-center gap-2 text-sm font-body font-medium text-charcoal-700 mb-1.5">
                  {field.icon} {field.label}
                </label>
                <input
                  type="text"
                  value={settings[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  data-testid={`settings-${field.key}`}
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                />
                <p className="text-xs text-charcoal-500 mt-1 italic">Used in the Gallery page footer to link visitors to your channel.</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-lg font-heading text-saffron-700 mb-4 pb-2 border-b border-saffron-100">
            Contact Details & Address
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {contactFields.map((field) => (
              <div key={field.key}>
                <label className="flex items-center gap-2 text-sm font-body font-medium text-charcoal-700 mb-1.5">
                  {field.icon} {field.label}
                </label>
                <input
                  type="text"
                  value={settings[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  data-testid={`settings-${field.key}`}
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                />
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {addressFields.map((field) => (
              <div key={field.key}>
                <label className="flex items-center gap-2 text-sm font-body font-medium text-charcoal-700 mb-1.5">
                  {field.icon} {field.label}
                </label>
                <textarea
                  rows={3}
                  value={settings[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  data-testid={`settings-${field.key}`}
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm leading-relaxed"
                />
                <p className="text-xs text-charcoal-500 mt-1 italic">Tip: press Enter to break onto a new line.</p>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-saffron-50/60 border border-saffron-100 rounded-lg p-4">
            <label className="flex items-center gap-2 text-sm font-body font-medium text-charcoal-800 mb-2">
              <MapPin size={18} className="text-saffron-600" />
              Address to display on the Contact page
            </label>
            <p className="text-xs text-charcoal-500 mb-3">
              Choose which office address appears in the <strong>Contact page</strong> info/map block. Both addresses always appear in the footer.
            </p>
            <select
              value={settings.contact_page_address_display || 'registered'}
              onChange={(e) => handleChange('contact_page_address_display', e.target.value)}
              data-testid="settings-contact_page_address_display"
              className="w-full px-4 py-3 rounded-lg border border-charcoal-200 bg-white focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
            >
              <option value="registered">Registered Office only</option>
              <option value="admin">Administrative Office only</option>
              <option value="both">Show both addresses</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
          <h3 className="text-lg font-heading text-charcoal-800 mb-2 flex items-center gap-2">
            <User size={20} className="text-saffron-600" /> Change Admin Email
          </h3>
          <p className="text-sm text-charcoal-500 mb-4">Current: <span className="font-medium">{user?.email}</span></p>
          <form onSubmit={handleChangeEmail} className="space-y-3">
            <input
              type="password"
              placeholder="Current Password"
              required
              value={emailForm.current_password}
              onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
              data-testid="change-email-password"
              className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
            />
            <input
              type="email"
              placeholder="New Email Address"
              required
              value={emailForm.new_email}
              onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
              data-testid="change-email-new"
              className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
            />
            <button
              type="submit"
              disabled={changingEmail}
              data-testid="change-email-submit"
              className="w-full bg-saffron-600 text-white rounded-lg px-4 py-2.5 hover:bg-saffron-500 transition-colors disabled:opacity-50"
            >
              {changingEmail ? 'Updating...' : 'Update Email'}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
          <h3 className="text-lg font-heading text-charcoal-800 mb-2 flex items-center gap-2">
            <Lock size={20} className="text-saffron-600" /> Change Password
          </h3>
          <p className="text-sm text-charcoal-500 mb-4">Update your admin password securely.</p>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                placeholder="Current Password"
                required
                value={passwordForm.current}
                onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                data-testid="change-password-current"
                className="w-full px-4 py-3 pr-10 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
              />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-3 text-charcoal-500">
                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                placeholder="New Password (min 6 characters)"
                required
                minLength={6}
                value={passwordForm.new}
                onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                data-testid="change-password-new"
                className="w-full px-4 py-3 pr-10 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
              />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-3 text-charcoal-500">
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <input
              type={showNew ? 'text' : 'password'}
              placeholder="Confirm New Password"
              required
              minLength={6}
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
              data-testid="change-password-confirm"
              className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
            />
            <button
              type="submit"
              disabled={changingPassword}
              data-testid="change-password-submit"
              className="w-full bg-saffron-600 text-white rounded-lg px-4 py-2.5 hover:bg-saffron-500 transition-colors disabled:opacity-50"
            >
              {changingPassword ? 'Updating...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
